import { useState, useEffect } from 'react';
import { Settings, Menu } from 'lucide-react';
import { supabase } from '../shared/supabase';

// ✅ Import centralized components
import Header from '../shared/components/Header';

// ✅ Import centralized theme
import { 
  gradients, 
  buttonStyles, 
  cardStyles, 
  colors,
  shadows,
  avatarStyles 
} from '../shared/config/theme';
import { getJourneyConfig } from '../shared/constants/journeyConfig';

const LibraryPage = ({ user, userProfile, onNavigate, onShowAuth, isPremium = false, streakCount = 0 }) => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedContent, setSelectedContent] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const categories = [
    { id: 'all', label: 'All', emoji: '✨' },
    { id: 'Confidence', label: 'Confidence', emoji: '💪' },
    { id: 'Love', label: 'Love', emoji: '❤️' },
    { id: 'Abundance', label: 'Abundance', emoji: '✨' },
    { id: 'Healing', label: 'Healing', emoji: '🌙' },
    { id: 'Gratitude', label: 'Gratitude', emoji: '🙏' },
    { id: 'Focus', label: 'Focus', emoji: '🎯' },
    { id: 'Clarity', label: 'Clarity', emoji: '💎' },
    { id: 'Joy', label: 'Joy', emoji: '☀️' },
    { id: 'Courage', label: 'Courage', emoji: '🦁' }
  ];

  const types = [
    { id: 'all', label: 'All Content' },
    { id: 'video', label: 'Videos' },
    { id: 'audio', label: 'Audio' },
    { id: 'pdf', label: 'Guides' }
  ];

  // ✅ Navigation handlers for Header component
  const handleMenuClick = () => {
    console.log('Menu clicked - navigating to more');
    if (onNavigate) {
      onNavigate('more');
    } else {
      console.error('onNavigate function not provided');
    }
  };

  const handleProgressClick = () => {
    console.log('Progress clicked - navigating to progress');
    if (onNavigate) {
      onNavigate('progress');
    } else {
      console.error('onNavigate function not provided');
    }
  };

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    try {
      const { data, error } = await supabase
        .from('premium_content')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setContents(data || []);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredContents = contents.filter(content => {
    const categoryMatch = selectedCategory === 'all' || content.category === selectedCategory;
    const typeMatch = selectedType === 'all' || content.type === selectedType;
    return categoryMatch && typeMatch;
  });

  const isPremiumUser = userProfile?.is_premium === true;

  const handleContentClick = (content) => {
    if (!user) {
      if (onShowAuth) onShowAuth();
      return;
    }
    if (content.is_free || isPremiumUser) {
      setSelectedContent(content);
    } else {
      setShowUpgradeModal(true);
    }
  };

  const handleUpgrade = async () => {
    if (!user) {
      alert('Please sign in to upgrade to premium');
      return;
    }

    try {
      setShowUpgradeModal(false);
      setShowUpgradeModal(true);
    } catch (error) {
      console.error('❌ Payment error:', error);
      alert('Payment failed. Please try again.');
      setShowUpgradeModal(true);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return '🎬';
      case 'audio': return '🎵';
      case 'pdf': return '📄';
      default: return '📦';
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-dream-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-700 text-xl">Loading library...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradients.background} pb-24`}>
      
      {/* ✅ Centralized Header Component with Fixed Navigation */}
      <Header
        user={user}
        userProfile={userProfile}
        isPremium={isPremium}
        streakCount={streakCount}
        onMenuClick={handleMenuClick}
        onProgressClick={handleProgressClick}
        showProgress={!!user}
        subtitle="Explore premium content"
      />

      {/* ✅ Category Filter - Using theme gradients */}
      <div className="max-w-6xl mx-auto px-6 py-4 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {categories.map(cat => {
            // Get journey-specific config for dynamic colors
            const journeyStyle = getJourneyConfig(cat.id);
            
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === cat.id
                    ? `bg-gradient-to-r ${gradients.button} text-white shadow-lg scale-105`
                    : 'bg-white/90 text-gray-700 hover:bg-white hover:shadow-md border border-dream-100'
                }`}
              >
                {cat.emoji} {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ✅ Type Filter - Using theme styles */}
      <div className="max-w-6xl mx-auto px-6 pb-4">
        <div className="flex gap-2">
          {types.map(type => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`flex-1 py-2 rounded-lg transition-all duration-300 ${
                selectedType === type.id
                  ? 'bg-white text-dream-600 font-semibold shadow-md border border-dream-200'
                  : 'bg-white/50 text-gray-600 hover:bg-white/80 border border-dream-100'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* ✅ Content Grid - Using theme card styles */}
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredContents.length === 0 ? (
          <div className="col-span-full">
            <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} p-12 ${cardStyles.default.shadow} ${cardStyles.default.border} text-center`}>
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-gray-700 text-xl font-semibold mb-2">No content found</h3>
              <p className="text-gray-500">Try adjusting your filters to see more content</p>
            </div>
          </div>
        ) : (
          filteredContents.map(content => {
            // Get journey-specific styling for content cards
            const journeyStyle = getJourneyConfig(content.category);
            
            return (
              <div
                key={content.id}
                onClick={() => handleContentClick(content)}
                className={`${cardStyles.default.background} ${cardStyles.default.rounded} overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 ${cardStyles.default.shadow} hover:shadow-xl ${cardStyles.default.border} relative`}
              >
                <div className={`relative h-48 bg-gradient-to-br ${journeyStyle.cardGradient}`}>
                  {content.thumbnail_url ? (
                    <img
                      src={content.thumbnail_url}
                      alt={content.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl">
                      {getTypeIcon(content.type)}
                    </div>
                  )}

                  {!content.is_free && !isPremiumUser && user && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🔒</div>
                        <div className="text-white font-bold">Premium</div>
                      </div>
                    </div>
                  )}

                  {!user && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🔐</div>
                        <div className="text-white font-bold">Sign In Required</div>
                      </div>
                    </div>
                  )}

                  <div className="absolute top-3 left-3 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm">
                    {getTypeIcon(content.type)} {content.type}
                  </div>

                  {content.is_free && (
                    <div className="absolute top-3 right-3 bg-green-500 px-3 py-1 rounded-full text-white text-sm font-bold shadow-md">
                      FREE
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <h3 className="text-gray-800 font-semibold text-lg mb-2 line-clamp-2">
                    {content.title}
                  </h3>
                  {content.description && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {content.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className={`${journeyStyle.textColor} font-medium`}>
                      {categories.find(c => c.id === content.category)?.emoji} {content.category}
                    </span>
                    {content.duration && (
                      <span className="text-gray-500">{content.duration}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ✅ Content Detail Modal - Using centralized theme */}
      {selectedContent && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <div className={`${cardStyles.soft.background} ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-dream-100`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{selectedContent.title}</h2>
                <button
                  onClick={() => setSelectedContent(null)}
                  className="text-gray-400 hover:text-gray-600 text-3xl transition-colors"
                >
                  ×
                </button>
              </div>

              {selectedContent.type === 'video' && (
                <video controls className="w-full rounded-lg mb-4" src={selectedContent.content_url}>
                  Your browser does not support video playback.
                </video>
              )}

              {selectedContent.type === 'audio' && (
                <div className="bg-white/80 rounded-lg p-8 mb-4 border border-dream-100">
                  <div className="text-center mb-4">
                    <div className="text-6xl mb-4">🎵</div>
                    <h3 className="text-gray-900 text-xl font-semibold">{selectedContent.title}</h3>
                  </div>
                  <audio controls className="w-full" src={selectedContent.content_url} />
                </div>
              )}

              {selectedContent.type === 'pdf' && (
                <div className="bg-white/80 rounded-lg p-4 mb-4 border border-dream-100">
                  <iframe
                    src={selectedContent.content_url}
                    className="w-full h-96 rounded-lg"
                    title={selectedContent.title}
                  />
                  <a
                    href={selectedContent.content_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center mt-4 text-dream-500 hover:text-dream-600 font-medium"
                  >
                    Open in new tab →
                  </a>
                </div>
              )}

              {selectedContent.description && (
                <p className="text-pearl-600 mb-4 leading-relaxed">{selectedContent.description}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Upgrade Modal - Using centralized theme */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <div className={`${cardStyles.soft.background} ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} max-w-md w-full p-8 text-center border border-dream-200`}>
            <div className="text-6xl mb-4">✨</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Unlock Premium Content</h2>
            <p className="text-pearl-600 mb-6 leading-relaxed">
              Get unlimited access to all videos, audio guides, and PDF resources with our premium subscription.
            </p>

            <div className="bg-dream-50 border border-dream-200 rounded-xl p-6 mb-6">
              <div className="text-4xl font-bold text-gray-900 mb-2">$4.99</div>
              <div className="text-pearl-500">per month</div>
            </div>

            <div className="text-left mb-6 space-y-2">
              <div className="flex items-center text-gray-900">
                <span className="mr-2 text-dream-500">✓</span> Unlimited manifestation sessions
              </div>
              <div className="flex items-center text-gray-900">
                <span className="mr-2 text-dream-500">✓</span> Access to all premium content
              </div>
              <div className="flex items-center text-gray-900">
                <span className="mr-2 text-dream-500">✓</span> New content added regularly
              </div>
              <div className="flex items-center text-gray-900">
                <span className="mr-2 text-dream-500">✓</span> Cancel anytime
              </div>
            </div>

            <button
              onClick={handleUpgrade}
              className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-4 mb-3`}
            >
              Upgrade to Premium
            </button>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="w-full text-gray-600 hover:text-gray-900 py-2 transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default LibraryPage;